// Collapses the articles in a time window into one point per storyline.
//
// The backend groups articles into narrow events (one occurrence) and events
// into storylines (successive stages). The globe shows each storyline once,
// at its current stage: the latest stage covered by at least two outlets that
// had started by the end of the window (a lone late opinion piece shouldn't
// displace the real latest development), else its best-covered stage.
// Articles without an event (older or not yet grouped) stay as they are.

export const MAX_COVERAGE_BOOST = 3;

// Height factor for an event covered by n distinct outlets: 1 for one outlet,
// with diminishing returns, capped so stories every outlet picks up don't
// flatten regions that have only one feed.
export function coverageBoost(n) {
  return Math.min(MAX_COVERAGE_BOOST, 1 + Math.log2(Math.max(1, n || 1)));
}

const outletOf = (p) => p.source || p.link?.split("/")[2] || p.article_id;

// eventMeta(eventId) → { storylineId, start, sources, title } from the
// timeline data, or undefined; storylineEvents(storylineId) → all known
// events of a storyline as [{ eventId, start, sources, title }].
export function collapseStorylines(points, { endMs = Date.now(), eventMeta = () => undefined, storylineEvents = () => [] } = {}) {
  const byEvent = new Map();
  const loose = [];
  for (const p of points) {
    if (!p.eventId) { loose.push(p); continue; }
    let e = byEvent.get(p.eventId);
    if (!e) byEvent.set(p.eventId, (e = { eventId: p.eventId, storylineId: p.storylineId, points: [] }));
    e.points.push(p);
  }

  const byStoryline = new Map();
  for (const e of byEvent.values()) {
    const meta = eventMeta(e.eventId) || {};
    e.storylineId = e.storylineId || meta.storylineId || e.eventId;
    e.start = meta.start ?? Math.min(...e.points.map((p) => p.publishedAt ?? endMs));
    e.sources = Math.max(meta.sources || 0, new Set(e.points.map(outletOf)).size);
    e.title = meta.title || e.points.find((p) => p.title)?.title;
    if (!byStoryline.has(e.storylineId)) byStoryline.set(e.storylineId, []);
    byStoryline.get(e.storylineId).push(e);
  }

  const dots = [];
  for (const [storylineId, events] of byStoryline) {
    const started = events.filter((e) => e.start <= endMs);
    const pool = started.length ? started : events;
    const covered = pool.filter((e) => e.sources >= 2);
    const current = covered.length
      ? covered.reduce((a, b) => (b.start > a.start ? b : a))
      : pool.reduce((a, b) => (b.points.length > a.points.length ? b : a));
    const articles = [...current.points].sort((a, b) => (b.val || 5) - (a.val || 5) || (a.publishedAt || 0) - (b.publishedAt || 0));
    const lead = articles[0];

    // Earlier stages: every known event of the storyline that started before
    // this one, from the timeline data where available.
    const known = new Map(storylineEvents(storylineId).map((e) => [e.eventId, e]));
    for (const e of events) if (!known.has(e.eventId)) known.set(e.eventId, e);
    const earlier = [...known.values()]
      .filter((e) => e.eventId !== current.eventId && e.start != null && e.start < current.start)
      .sort((a, b) => a.start - b.start)
      .map(({ eventId, start, title, sources }) => ({ eventId, start, title, sources }));

    const story = { storylineId, eventId: current.eventId, title: current.title, sources: current.sources, earlier };
    dots.push({
      ...lead,
      article_id: `story:${storylineId}`,
      // Column weight is val² × boost: coverage scales it once, not squared
      // with the importance.
      importance: Math.max(...articles.map((a) => a.val || 5)),
      val: Math.max(...articles.map((a) => a.val || 5)),
      boost: coverageBoost(current.sources),
      articles: articles.map((a) => ({ ...a, story })),
      story,
    });
  }
  return dots.concat(loose);
}

// The articles to list for a set of globe points (collapsed or not).
export function articlesOfPoints(points) {
  return points.flatMap((p) => p.articles || [p]);
}
